import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import routes from "../../../../routes/routes";
import { KeyHandoverReturnScheduleProvider } from "../context/KeyHandoverReturnScheduleContext";
import KeyHandoverReturnScheduleStep from "../steps/KeyHandoverReturnScheduleStep";
import KeyHandoverNavigateNextAction from "../common/util/KeyHandoverNavigateNextAction";
import { parseBookingQuery } from "../common/url/bookingQuery";
import { useReturnScheduleHydration } from "../hooks/useReturnScheduleHydration";
import { useReturnScheduleUrlSync } from "../hooks/useReturnScheduleUrlSync";
import { useScheduleHydration } from "../hooks/useScheduleHydration";
import { useScheduleUrlSync } from "../hooks/useScheduleUrlSync";
import { useLocationHydration } from "../hooks/useLocationHydration";
import { useServiceHydration } from "../hooks/useServiceHydration";
import type { RootState } from "../../../../redux/store";
import { useAppDispatch } from "../../../../redux/useDispatch";
import { setUseCase, setAvailabilities } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { availabilitiesApiSlice } from "../../../../api/availabilities/availabilitiesApiSlice";
import { KeyHandoverPageLayout } from "../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../components/KeyHandoverContentWrapper";
import { Spinner } from "../../../../components/ui/spinner";
import { useBookingWindow } from "../../common/hooks/useBookingWindow";

/**
 * KeyHandoverReturnSchedulePage Component
 * ---------------------------------------------------------------
 * Manages the return schedule selection step in the key handover booking flow.
 *
 * @remarks
 * This page handles:
 * - Location and service hydration from URL
 * - Return schedule (returnDate, returnTime, returnDays) hydration and URL synchronization
 * - Availability fetching for the selected service
 * - Navigation guards to ensure prerequisite data exists
 * - Rendering the return schedule selection UI
 *
 * @example
 * Route: /key-handover/return-schedule?locationId=loc-123&serviceId=svc-456&returnDate=2025-12-26&returnTime=10:00&returnDays=2
 */
const KeyHandoverReturnSchedulePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Redux selectors
  const selectedGroup = useSelector((s: RootState) => s.bookingCreation.selectedGroup);
  const availabilities = useSelector((s: RootState) => s.bookingCreation.availabilities);
  const orderData = useSelector((s: RootState) => s.bookingCreation.orderData);

  // Initialize use case once
  useEffect(() => {
    dispatch(setUseCase({ useCase: "keys" }));
  }, [dispatch]);

  // Guard: require location & service in URL
  useEffect(() => {
    const q = parseBookingQuery(location.search);
    if (!q.locationId) {
      navigate({ pathname: routes.keyHandoverLocationSelectionPath, search: location.search }, { replace: true });
      return;
    }
    if (!q.serviceId) {
      navigate({ pathname: routes.keyHandoverServiceSelectionPath, search: location.search }, { replace: true });
    }
  }, [location.search, navigate]);

  // The configured booking horizon for this use case — replaces the hardcoded
  // `add(1, "months").endOf("month")` this file computed inline. One month is
  // the DEFAULT here, so an unconfigured tenant requests exactly the same range
  // as before. `earliest` carries the lead buffer, which defaults to 0 for this
  // use case (the customer may book now), and is the same rule the app API
  // enforces (ADR-0020, `backend/src/core/lib/bookingWindow.ts`).
  const bookingWindow = useBookingWindow("keys");

  // Fetch availabilities when service is selected
  useEffect(() => {
    if (!selectedGroup?.id || availabilities) return;

    const fromDate = bookingWindow.earliest.toDate();
    const toDate = bookingWindow.latest.toDate();

    dispatch(
      availabilitiesApiSlice.endpoints.fetchAssetGroupAvailabilities.initiate({
        assetGroupId: selectedGroup.id,
        from: fromDate,
        to: toDate,
      })
    )
      .unwrap()
      .then((data) => {
        dispatch(setAvailabilities(data?.availabilities ?? ({} as any)));
      })
      .catch(() => {
        dispatch(setAvailabilities({} as any));
      });
  }, [selectedGroup?.id, availabilities, dispatch]);

  // Validation for enabling Next button
  const returnScheduleValid = Boolean(orderData.keyReturnFromDate && orderData.keyReturnToDate);
  const hasAvailabilities = Boolean(
    availabilities && typeof availabilities === "object" && Object.keys(availabilities).length > 0
  );

  // Determine next route based on customerAgreementId in URL
  const params = new URLSearchParams(location.search);
  const customerAgreementId = params.get("customerAgreementId");
  let nextRoute: string;
  if (customerAgreementId) {
    nextRoute = routes.keyHandoverGuestDetailsPath;
  } else {
    nextRoute = routes.keyHandoverHostDetailsPath;
  }

  // Bottom actions for return schedule page
  const bottomActions = Boolean(selectedGroup) && hasAvailabilities && (
    <KeyHandoverNavigateNextAction
      to={nextRoute}
      enabled={Boolean(selectedGroup) && hasAvailabilities && returnScheduleValid}
      bounceKey={returnScheduleValid ? selectedGroup?.id ?? null : null}
    />
  );

  return (
    <KeyHandoverPageLayout
      headerText={t("book_location.keys.return_data.header_text", {
        defaultValue: "Schlüsselrückgabe planen",
      })}
      backTo={routes.keyHandoverSchedulePath}
      bottomActions={bottomActions}
    >
      <KeyHandoverContentWrapper>
        <ReturnScheduleContent />
      </KeyHandoverContentWrapper>
    </KeyHandoverPageLayout>
  );
};

/**
 * Content component that handles all hydration and URL synchronization.
 *
 * @remarks
 * Separated component to ensure hooks execute in correct lifecycle order:
 * 1. Location hydration (from useLocationHydration hook)
 * 2. Service hydration (from useServiceHydration hook)
 * 3. Pickup schedule hydration and sync (to preserve URL params)
 * 4. Return schedule hydration and sync
 */
const ReturnScheduleContent: React.FC = () => {
  const scheduleHydratedRef = useRef(true);
  const returnScheduleHydratedRef = useRef(true);

  // Hydrate location and service from URL
  const locationHydrated = useLocationHydration();
  useServiceHydration();

  // Hydrate pickup schedule from URL (to preserve pickup parameters)
  useScheduleHydration(locationHydrated);

  // Sync pickup schedule changes back to URL (to preserve pickup parameters)
  useScheduleUrlSync(scheduleHydratedRef);

  // Hydrate return schedule from URL
  useReturnScheduleHydration(locationHydrated);

  // Sync return schedule changes back to URL
  useReturnScheduleUrlSync(returnScheduleHydratedRef);

  // Show loading spinner while location is hydrating
  if (!locationHydrated) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <KeyHandoverReturnScheduleProvider>
      <KeyHandoverReturnScheduleStep />
    </KeyHandoverReturnScheduleProvider>
  );
};

export default KeyHandoverReturnSchedulePage;
